import {
  Box,
  Button,
  Chip,
  FormHelperText,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MuiAlert from "@mui/material/Alert";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Header from "../../components/Header";
import { getWorshipCenter, updateWorshipCenter } from "./../../ApiConfig.jsx";
import * as yup from "yup";
import { TIME_OPTIONS } from "../../Utils/worshipTime";

// ─── Constants ─────────────────────────────────────────────────────────────────
const DAYS = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];

const MAX_DESCRIPTION_WORDS = 50;
const countWords = (text = "") =>
  text.trim().split(/\s+/).filter(Boolean).length;

const phoneRegExp = /^((\+[1-9]{1,4}[ -]?)|(\([0-9]{2,3}\)[ -]?)|([0-9]{2,4})[ -]?)*?[0-9]{3,4}[ -]?[0-9]{3,4}$/;

const fieldSchema = yup.object().shape({
  churchName: yup.string().required("Church name is required"),
  address:    yup.string().required("Address is required"),
  email:      yup.string().email("Invalid email").required("Email is required"),
  tel:        yup.string().matches(phoneRegExp, "Phone number is not valid").max(15, "Phone number must be at most 15 digits").required("Phone is required"),
  description: yup
    .string()
    .required("Description is required")
    .test(
      "max-words",
      `Description must be ${MAX_DESCRIPTION_WORDS} words or less`,
      (value) => countWords(value) <= MAX_DESCRIPTION_WORDS,
    ),
  longitude:  yup.number().typeError("Must be a number").required("Required"),
  latitude:   yup.number().typeError("Must be a number").required("Required"),
  paster:     yup.string().required("Required"),
});

// ─── Open Hour Row ──────────────────────────────────────────────────────────────
const OpenHourRow = ({ row, index, onChange, onRemove }) => (
  <Box
    display="flex" gap="12px" alignItems="center" flexWrap="wrap"
    sx={{ backgroundColor: "rgba(104,112,250,0.07)", borderRadius: "8px", p: "12px", mb: "10px" }}
  >
    <Select size="small" variant="filled" value={row.day}
      onChange={(e) => onChange(index, "day", e.target.value)} sx={{ minWidth: 130 }}>
      {DAYS.map((d) => <MenuItem key={d} value={d.toLowerCase()}>{d}</MenuItem>)}
    </Select>
    <Select size="small" variant="filled" value={row.from} displayEmpty
      onChange={(e) => onChange(index, "from", e.target.value)} sx={{ minWidth: 110 }}>
      <MenuItem disabled value="">From</MenuItem>
      {TIME_OPTIONS.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
    </Select>
    <Typography sx={{ color: "#aaa" }}>–</Typography>
    <Select size="small" variant="filled" value={row.to} displayEmpty
      onChange={(e) => onChange(index, "to", e.target.value)} sx={{ minWidth: 110 }}>
      <MenuItem disabled value="">To</MenuItem>
      {TIME_OPTIONS.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
    </Select>
    <IconButton onClick={() => onRemove(index)} size="small" sx={{ color: "#f44336", ml: "auto" }}>
      <DeleteOutlineIcon />
    </IconButton>
  </Box>
);

// ─── Main Component ─────────────────────────────────────────────────────────────
function EditWorship() {
  const { id }       = useParams();
  const navigate     = useNavigate();

  // Basic fields
  const [fields, setFields] = useState({
    churchName: "", address: "", email: "", tel: "",
    description: "", noOfUsers: "0", longitude: "", latitude: "", paster: "",
  });
  const [status,       setStatus]       = useState("active");
  const [currentImage, setCurrentImage] = useState("");
  const [newImage,     setNewImage]     = useState(null);
  const [errors,       setErrors]       = useState({});

  // Open Hours: [{ day, from, to }]
  const [openHours,      setOpenHours]      = useState([{ day: "sunday", from: "", to: "" }]);
  const [openHoursError, setOpenHoursError] = useState("");

  // Services: [{ day, from, to, names: string[] }]
  const [services,      setServices]      = useState([{ day: "sunday", from: "", to: "", names: [] }]);
  const [servicesError, setServicesError] = useState("");
  const [draftNames,    setDraftNames]    = useState({ 0: "" });

  // Snackbar
  const [openSnackbar,  setOpenSnackbar]  = useState(false);
  const [alertSeverity, setAlertSeverity] = useState("success");
  const [alertMessage,  setAlertMessage]  = useState("");
  const [isLoading,     setIsLoading]     = useState(false);

  // ── Fetch existing data ──────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        const data = await getWorshipCenter(id);
        if (!data.status) return;
        const r = data.result;

        setStatus(r.status || "active");
        setCurrentImage(r.imageURL || "");

        setFields({
          churchName: r.church      || "",
          address:    r.address     || "",
          email:      r.email       || "",
          tel:        r.tel         || "",
          description: r.description || "",
          noOfUsers:  r.noOfUsers   || "0",
          longitude:  r.longtitude  || "",
          latitude:   r.latitude    || "",
          paster:     r.paster      || "",
        });

        // Parse openHours — API returns array of [day,from,to] or JSON string
        const rawOH = r.openHours;
        const parsedOH = parseJsonField(rawOH);
        if (Array.isArray(parsedOH) && parsedOH.length > 0) {
          setOpenHours(parsedOH.map(([day, from, to]) => ({ day, from, to })));
        }

        // Parse service — API returns array of [day,from,to,namesCSV] or JSON string
        const rawSvc = r.service;
        const parsedSvc = parseJsonField(rawSvc);
        if (Array.isArray(parsedSvc) && parsedSvc.length > 0) {
          const slots = parsedSvc.map(([day, from, to, namesCSV], i) => ({
            day, from, to,
            names: namesCSV ? namesCSV.split(",").map((n) => n.trim()).filter(Boolean) : [],
          }));
          setServices(slots);
          const drafts = {};
          slots.forEach((_, i) => { drafts[i] = ""; });
          setDraftNames(drafts);
        }
      } catch (err) {
        console.error(err);
      }
    };
    load();
  }, [id]);

  // Safely parse either a JS array or a JSON string
  const parseJsonField = (val) => {
    if (Array.isArray(val)) return val;
    if (typeof val === "string") {
      try { return JSON.parse(val); } catch { return []; }
    }
    return [];
  };

  // ── Field validation ─────────────────────────────────────────────────────────
  const validateField = (field, value) => {
    fieldSchema.validateAt(field, { ...fields, [field]: value })
      .then(() => setErrors((p) => ({ ...p, [field]: "" })))
      .catch((e) => setErrors((p) => ({ ...p, [field]: e.message })));
  };

  const handleFieldChange = (field, value) => {
    setFields((p) => ({ ...p, [field]: value }));
    validateField(field, value);
  };

  // ── Open Hours handlers ──────────────────────────────────────────────────────
  const handleOHChange  = (i, f, v) => { const u=[...openHours]; u[i][f]=v; setOpenHours(u); };
  const addOHRow        = () => setOpenHours((p) => [...p, { day:"sunday", from:"", to:"" }]);
  const removeOHRow     = (i) => setOpenHours((p) => p.filter((_,j) => j!==i));

  // ── Service handlers ─────────────────────────────────────────────────────────
  const handleSlotChange = (i, f, v) => { const u=[...services]; u[i][f]=v; setServices(u); };

  const addSlot = () => {
    const ni = services.length;
    setServices((p) => [...p, { day:"sunday", from:"", to:"", names:[] }]);
    setDraftNames((p) => ({ ...p, [ni]: "" }));
  };

  const removeSlot = (i) => {
    setServices((p) => p.filter((_,j) => j!==i));
    setDraftNames((p) => {
      const u={};
      Object.keys(p).forEach((k) => {
        const ki=parseInt(k);
        if (ki<i) u[ki]=p[k];
        else if (ki>i) u[ki-1]=p[k];
      });
      return u;
    });
  };

  const commitName = (si) => {
    const name=(draftNames[si]||"").trim();
    if (!name) return;
    setServices((p) => {
      const u=[...p];
      u[si]={ ...u[si], names:[...u[si].names, name] };
      return u;
    });
    setDraftNames((p) => ({ ...p, [si]:"" }));
    setServicesError("");
  };

  const removeName = (si, ni) => {
    setServices((p) => {
      const u=[...p];
      u[si]={ ...u[si], names: u[si].names.filter((_,j) => j!==ni) };
      return u;
    });
  };

  const getServicesWithDrafts = () =>
    services.map((slot, si) => {
      const pendingName = (draftNames[si] || "").trim();
      if (!pendingName) return slot;
      return { ...slot, names: [...slot.names, pendingName] };
    });

  // ── Validate dynamic sections ─────────────────────────────────────────────
  const validateDynamic = () => {
    const servicesToValidate = getServicesWithDrafts();
    let ok = true;
    if (openHours.some((r) => !r.from || !r.to)) {
      setOpenHoursError("Please complete all open hour entries."); ok=false;
    } else setOpenHoursError("");

    if (servicesToValidate.some((r) => !r.from || !r.to || r.names.length===0)) {
      setServicesError("Each slot needs from/to times and at least one service name."); ok=false;
    } else setServicesError("");

    return ok;
  };

  // ── Submit ───────────────────────────────────────────────────────────────────
  const handleUpdate = async () => {
    // Validate basic fields
    try {
      await fieldSchema.validate(fields, { abortEarly: false });
      setErrors({});
    } catch (yupErr) {
      const errs={};
      yupErr.inner.forEach((e) => { errs[e.path]=e.message; });
      setErrors(errs);
      return;
    }
    if (!validateDynamic()) return;

    const servicesToSave = getServicesWithDrafts();
    setServices(servicesToSave);
    setDraftNames({});

    const openHoursPayload = JSON.stringify(openHours.map((r) => [r.day, r.from, r.to]));
    const servicePayload   = JSON.stringify(servicesToSave.map((r) => [r.day, r.from, r.to, r.names.join(",")]));

    const formData = new FormData();
    formData.append("church",     fields.churchName);
    formData.append("address",    fields.address);
    formData.append("email",      fields.email);
    formData.append("tel",        fields.tel);
    formData.append("noOfUsers",  fields.noOfUsers || "0");
    formData.append("description", fields.description);
    formData.append("longtitude", fields.longitude);
    formData.append("latitude",   fields.latitude);
    formData.append("paster",     fields.paster);
    formData.append("status",     status);
    formData.append("openHours",  openHoursPayload);
    formData.append("service",    servicePayload);
    if (newImage) formData.append("image", newImage);

    setIsLoading(true);
    try {
      const res = await updateWorshipCenter(id, formData);
      if (res.status) {
        setAlertSeverity("success");
        setAlertMessage("Worship center updated successfully");
        setTimeout(() => navigate("/worship-centers"), 2500);
      }
    } catch (err) {
      setAlertSeverity("error");
      setAlertMessage(`Failed to update: ${err.message}`);
    } finally {
      setOpenSnackbar(true);
      setIsLoading(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <Box m="20px" height="70vh" overflow="auto" paddingRight="20px">
      <Header title={`Edit Worship Center #${id}`} subtitle="" />
      <Box ml="40px">

        {/* ── Basic Fields ── */}
        <Grid container spacing={2}>
          {[
            { label: "Church Name*",    field: "churchName", type: "text"   },
            { label: "Address*",        field: "address",    type: "text"   },
            { label: "Email*",          field: "email",      type: "email"  },
            { label: "Telephone*",      field: "tel",        type: "text"   },
            { label: "Pastor*",         field: "paster",     type: "text"   },
            { label: "Latitude*",       field: "latitude",   type: "text"   },
            { label: "Longitude*",      field: "longitude",  type: "text"   },
          ].map(({ label, field, type }) => (
            <Grid item xs={12} md={6} key={field}>
              <Typography variant="subtitle1" fontWeight="bold">{label}</Typography>
              <TextField
                fullWidth variant="filled" type={type}
                value={fields[field]}
                onChange={(e) => handleFieldChange(field, e.target.value)}
                error={!!errors[field]}
                helperText={errors[field]}
              />
            </Grid>
          ))}

          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight="bold">
              Description* ({countWords(fields.description)}/{MAX_DESCRIPTION_WORDS} words)
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={3}
              variant="filled"
              value={fields.description}
              onChange={(e) => handleFieldChange("description", e.target.value)}
              error={!!errors.description}
              helperText={errors.description || `Write a short description in ${MAX_DESCRIPTION_WORDS} words or less.`}
              placeholder="Brief description of this worship center"
            />
          </Grid>

          {/* Status */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="bold">Status*</Typography>
            <Select fullWidth variant="filled" value={status} onChange={(e) => setStatus(e.target.value)}>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </Grid>

          {/* New Image upload */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="bold">Upload New Image</Typography>
            <TextField
              fullWidth variant="filled" type="file"
              inputProps={{ accept: "image/*" }}
              onChange={(e) => setNewImage(e.target.files[0])}
            />
            <Typography variant="caption" sx={{ color: "#888" }}>
              Leave blank to keep the current image.
            </Typography>
          </Grid>

          {/* Current Image */}
          {currentImage && (
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" fontWeight="bold">Current Image</Typography>
              <img
                src={currentImage} alt="Current"
                style={{ marginTop: "6px", maxWidth: "100%", maxHeight: "220px",
                  borderRadius: "8px", objectFit: "cover" }}
              />
            </Grid>
          )}
        </Grid>

        {/* ── Open Hours ── */}
        <Box mt="30px">
          <Box display="flex" alignItems="center" justifyContent="space-between" mb="12px">
            <Typography variant="h6" fontWeight="700">Open Hours</Typography>
            <Button startIcon={<AddCircleOutlineIcon />} onClick={addOHRow}
              sx={{ color: "#6870fa", textTransform: "none" }}>Add Row</Button>
          </Box>
          {openHours.map((row, i) => (
            <OpenHourRow key={i} row={row} index={i} onChange={handleOHChange} onRemove={removeOHRow} />
          ))}
          {openHoursError && (
            <FormHelperText style={{ color: "#f44336" }}>{openHoursError}</FormHelperText>
          )}
          <Typography variant="caption" sx={{ color: "#888", mt: "4px", display: "block" }}>
            Preview: {JSON.stringify(openHours.map((r) => [r.day, r.from, r.to]))}
          </Typography>
        </Box>

        {/* ── Services ── */}
        <Box mt="30px">
          <Box display="flex" alignItems="center" justifyContent="space-between" mb="12px">
            <Typography variant="h6" fontWeight="700">Services</Typography>
            <Button startIcon={<AddCircleOutlineIcon />} onClick={addSlot}
              sx={{ color: "#6870fa", textTransform: "none" }}>Add Slot</Button>
          </Box>

          {services.map((slot, si) => (
            <Box key={si} sx={{ backgroundColor: "rgba(104,112,250,0.07)", borderRadius: "8px", p: "14px", mb: "14px" }}>
              {/* Day + Time row */}
              <Box display="flex" gap="12px" alignItems="center" flexWrap="wrap" mb="12px">
                <Select size="small" variant="filled" value={slot.day}
                  onChange={(e) => handleSlotChange(si, "day", e.target.value)} sx={{ minWidth: 130 }}>
                  {DAYS.map((d) => <MenuItem key={d} value={d.toLowerCase()}>{d}</MenuItem>)}
                </Select>
                <Select size="small" variant="filled" value={slot.from} displayEmpty
                  onChange={(e) => handleSlotChange(si, "from", e.target.value)} sx={{ minWidth: 110 }}>
                  <MenuItem disabled value="">From</MenuItem>
                  {TIME_OPTIONS.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                </Select>
                <Typography sx={{ color: "#aaa" }}>–</Typography>
                <Select size="small" variant="filled" value={slot.to} displayEmpty
                  onChange={(e) => handleSlotChange(si, "to", e.target.value)} sx={{ minWidth: 110 }}>
                  <MenuItem disabled value="">To</MenuItem>
                  {TIME_OPTIONS.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                </Select>
                <IconButton onClick={() => removeSlot(si)} size="small" sx={{ color: "#f44336", ml: "auto" }}>
                  <DeleteOutlineIcon />
                </IconButton>
              </Box>

              {/* Chips */}
              {slot.names.length > 0 && (
                <Box display="flex" flexWrap="wrap" gap="6px" mb="10px">
                  {slot.names.map((name, ni) => (
                    <Chip key={ni} label={name} size="small"
                      onDelete={() => removeName(si, ni)}
                      sx={{
                        backgroundColor: "rgba(104,112,250,0.2)", fontWeight: 500,
                        "& .MuiChip-deleteIcon": { color: "#f44336" },
                      }}
                    />
                  ))}
                </Box>
              )}

              {/* Draft input */}
              <Box display="flex" gap="10px" alignItems="center">
                <TextField
                  size="small" variant="filled" sx={{ flexGrow: 1 }}
                  placeholder="Type a service name (press Enter or Add, or it saves on Update)"
                  value={draftNames[si] || ""}
                  onChange={(e) => setDraftNames((p) => ({ ...p, [si]: e.target.value }))}
                  onKeyDown={(e) => { if (e.key==="Enter") { e.preventDefault(); commitName(si); } }}
                />
                <Button variant="outlined" onClick={() => commitName(si)}
                  sx={{ color:"#6870fa", borderColor:"#6870fa", textTransform:"none",
                    whiteSpace:"nowrap", "&:hover":{ borderColor:"#3e4396", color:"#3e4396" } }}>
                  + Add
                </Button>
              </Box>

              <Typography variant="caption" sx={{ color:"#888", mt:"6px", display:"block" }}>
                Stored as: {JSON.stringify([slot.day, slot.from, slot.to, slot.names.join(",")])}
              </Typography>
            </Box>
          ))}

          {servicesError && (
            <FormHelperText style={{ color: "#f44336" }}>{servicesError}</FormHelperText>
          )}
        </Box>

        {/* ── Submit ── */}
        <Box display="flex" justifyContent="flex-end" mt="30px">
          <Button variant="contained" onClick={handleUpdate}
            sx={{ backgroundColor:"#6870fa", color:"white", fontSize:"16px", px:"32px",
              "&:hover":{ backgroundColor:"#3e4396" } }}>
            {isLoading ? "Updating..." : "Update Worship Center"}
          </Button>
        </Box>
      </Box>

      <Snackbar open={openSnackbar} autoHideDuration={5000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical:"top", horizontal:"right" }}>
        <MuiAlert onClose={() => setOpenSnackbar(false)} severity={alertSeverity}
          elevation={6} variant="filled" style={{ color:"white" }}>
          {alertSeverity==="success" ? "Success" : "Error"}: {alertMessage}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}

export default EditWorship;